import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { MarkdownContent } from '../components/MarkdownContent';
import { MediaViewer, ImageCard } from '../components/MediaViewer';
import { cn } from '../lib/utils';
import { getProductDisplayName, getUrlsFromJsonb } from '../lib/helpers';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  ImageIcon, 
  Loader2,
  ExternalLink,
  Download,
  FileText
} from 'lucide-react';

export default function StaticAdDetail() {
  const { ecomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [staticAd, setStaticAd] = useState(null);
  const [winningDetail, setWinningDetail] = useState(null);
  
  // Media viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState({ url: '', type: 'image', title: '' });
  const [allMedia, setAllMedia] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [productRes, staticAdRes, winningRes] = await Promise.all([
        supabase.from('products').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('static_ads').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_details').select('ecom_id, product_name').eq('ecom_id', ecomId).single(),
      ]);

      setProduct(productRes.data);
      setStaticAd(staticAdRes.data);
      setWinningDetail(winningRes.data);
    } catch (error) {
      console.error('Error fetching static ad:', error);
      toast.error('Failed to load static ad details');
    } finally {
      setLoading(false);
    }
  }, [ecomId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayName = getProductDisplayName(ecomId, winningDetail ? [winningDetail] : []);

  const openViewer = (url, title, mediaList = [], index = 0) => {
    setCurrentMedia({ url, type: 'image', title });
    setAllMedia(mediaList);
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const handleNavigate = (newIndex) => {
    if (allMedia[newIndex]) {
      setCurrentIndex(newIndex);
      setCurrentMedia({
        url: allMedia[newIndex].url,
        type: 'image',
        title: allMedia[newIndex].title
      });
    }
  };

  // Get all images from an ad set
  const getAdSetImages = (adNumber) => {
    const images = getUrlsFromJsonb(staticAd?.[`ad${adNumber}_images`]);
    return images.map((url, idx) => ({ url, title: `Ad Set ${adNumber} - Image ${idx + 1}` }));
  };

  // Get all images across all ad sets
  const getAllImages = () => {
    const allImages = [];
    for (let i = 1; i <= 8; i++) {
      const images = getAdSetImages(i);
      allImages.push(...images);
    }
    return allImages;
  };

  const handleDownloadAdSet = async (adNumber) => {
    const images = getAdSetImages(adNumber);
    for (let i = 0; i < images.length; i++) {
      try {
        const response = await fetch(images[i].url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ad_set_${adNumber}_image_${i + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error) {
        console.error(`Failed to download image ${i + 1}`);
      }
    }
    toast.success(`Downloaded ${images.length} images from Ad Set ${adNumber}`);
  };

  const handleDownloadAll = async () => {
    const allImages = getAllImages();
    toast.info(`Starting download of ${allImages.length} images...`);
    for (let i = 0; i < allImages.length; i++) {
      try {
        const response = await fetch(allImages[i].url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `static_ad_${i + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (error) {
        console.error(`Failed to download image ${i + 1}`);
      }
    }
    toast.success(`Downloaded ${allImages.length} images`);
  };

  if (loading) {
    return (
      <Layout productName="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!staticAd) {
    return (
      <Layout>
        <div className="text-center py-16">
          <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">Static Ad not found</p>
          <button
            onClick={() => navigate('/ads')}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg"
          >
            Back to Ads
          </button>
        </div>
      </Layout>
    );
  }

  const totalImages = getAllImages().length;

  return (
    <Layout productName={displayName}>
      <div data-testid="static-ad-detail">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ads')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-amber-600" />
                </div>
                
                <div>
                  <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Manrope']">
                    {displayName}
                  </h1>
                  <div className="flex items-center gap-3 mt-1">
                    <StatusBadge status={staticAd.status} lane="ads" />
                    <span className="text-sm text-slate-500">{totalImages} images • 8 ad sets</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {totalImages > 0 && (
                <button
                  onClick={handleDownloadAll}
                  className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download All ({totalImages})
                </button>
              )}
              {product?.url && (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Product
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Ad Strategy */}
        {staticAd.full_ad_strategy && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-slate-600" />
              <h2 className="text-xl font-bold text-[#1E1B4B]">Ad Strategy</h2>
            </div>
            <MarkdownContent 
              content={staticAd.full_ad_strategy} 
              title="Ad Strategy"
              maxHeight={200}
            />
          </div>
        )}

        {/* Ad Sets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(adNumber => {
            const images = getAdSetImages(adNumber);
            const allImagesFlat = getAllImages();
            
            return (
              <div key={adNumber} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-700">Ad Set {adNumber}</h3>
                  {images.length > 0 && (
                    <button
                      onClick={() => handleDownloadAdSet(adNumber)}
                      className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                      title="Download all images in this set"
                    >
                      <Download className="w-4 h-4 text-slate-500" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  {images.length > 0 ? (
                    images.slice(0, 4).map((img, idx) => {
                      const globalIndex = allImagesFlat.findIndex(i => i.url === img.url);
                      return (
                        <ImageCard
                          key={idx}
                          url={img.url}
                          label={`Image ${idx + 1}`}
                          onView={() => openViewer(img.url, img.title, allImagesFlat, globalIndex >= 0 ? globalIndex : 0)}
                        />
                      );
                    })
                  ) : (
                    [...Array(4)].map((_, idx) => (
                      <div key={idx} className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-slate-300" />
                      </div>
                    ))
                  )}
                </div>
                
                {images.length > 4 && (
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    +{images.length - 4} more images
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Media Viewer */}
        <MediaViewer
          open={viewerOpen}
          onOpenChange={setViewerOpen}
          mediaUrl={currentMedia.url}
          mediaType={currentMedia.type}
          title={currentMedia.title}
          allMedia={allMedia}
          currentIndex={currentIndex}
          onNavigate={handleNavigate}
        />
      </div>
    </Layout>
  );
}
